import React from 'react';
import { Link } from 'react-router-dom';
import { useStatistics } from '../hooks/useApi';
import { formatNumber, formatDate } from '../utils';
import LoadingSpinner from '../components/LoadingSpinner';
import StatsCard from '../components/StatsCard';
import Chart from '../components/Chart';
import './Dashboard.css';

const Dashboard: React.FC = () => {
  const { statistics, loading, error, refresh } = useStatistics();

  if (loading) {
    return <LoadingSpinner message="Carregando estatísticas..." />;
  }

  if (error) {
    return (
      <div className="dashboard-error">
        <div className="error-content">
          <h2>Erro ao carregar estatísticas</h2>
          <p>{error}</p>
          <button onClick={refresh} className="retry-button">
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  if (!statistics) {
    return (
      <div className="dashboard-empty">
        <h2>Nenhuma estatística disponível</h2>
        <p>Não há dados para exibir no momento.</p>
      </div>
    );
  }

  const { general, byRadio, byCategory, byMonth } = statistics;

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1>Dashboard</h1>
        <p>Painel de controle do sistema Radio Itajaí</p>
        <button onClick={refresh} className="refresh-button">
          🔄 Atualizar
        </button>
      </div>

      {/* Cards de estatísticas gerais */}
      <div className="stats-grid">
        <StatsCard
          title="Total de Análises"
          value={formatNumber(general.totalAnalyses)}
          icon="📊"
          color="blue"
        />
        <StatsCard
          title="Análises Negativas"
          value={formatNumber(general.totalNegative)}
          subtitle={`${general.percentageNegative}% do total`}
          icon="😞"
          color="red"
        />
        <StatsCard
          title="Análises Positivas"
          value={formatNumber(general.totalPositive)}
          subtitle={`${general.percentagePositive}% do total`}
          icon="😊"
          color="green"
        />
        <StatsCard
          title="Análises Neutras"
          value={formatNumber(general.totalNeutral)}
          subtitle={`${general.percentageNeutral}% do total`}
          icon="😐"
          color="gray"
        />
        <StatsCard
          title="Menções à Prefeitura"
          value={formatNumber(general.totalMentionedPrefecture)}
          icon="🏛️"
          color="purple"
        />
        <StatsCard
          title="E-mails Enviados"
          value={formatNumber(general.totalEmailSent)}
          icon="📧"
          color="orange"
        />
      </div>

      {/* Gráficos */}
      <div className="charts-section">
        <div className="charts-grid">
          {/* Gráfico por rádio */}
          <div className="chart-card full-width">
            <h3>Análises por Rádio</h3>
            <Chart
              type="bar"
              data={byRadio.map(item => ({
                label: item.radio,
                value: item.total,
                negativo: item.negativo,
                positivo: item.positivo,
                neutro: item.neutro
              }))}
            />
          </div>

          {/* Gráfico por categoria */}
          <div className="chart-card full-width">
            <h3>Análises por Categoria</h3>
            <Chart
              type="pie"
              data={byCategory.map(item => ({
                label: item.categoria || 'Sem categoria',
                value: item.total
              }))}
            />
          </div>

          {/* Gráfico mensal */}
          {/* <div className="chart-card full-width">
            <h3>Análises por Mês</h3>
            <Chart
              type="line"
              data={byMonth.map(item => ({
                label: item.month,
                value: item.total,
                negativo: item.negativo
              }))}
            />
          </div> */}
        </div>
      </div>

      {/* Ações rápidas */}
      <div className="quick-actions">
        <h3>Ações Rápidas</h3>
        <div className="actions-grid">
          <Link to="/analyses" className="action-card">
            <div className="action-icon">📋</div>
            <div className="action-content">
              <h4>Ver Todas as Análises</h4>
              <p>Acesse a lista completa de análises de áudio</p>
            </div>
          </Link>
          
          <Link to="/analyses?sentiment_type=negativo" className="action-card">
            <div className="action-icon">⚠️</div>
            <div className="action-content">
              <h4>Análises Negativas</h4>
              <p>Visualize apenas as análises com sentimento negativo</p>
            </div>
          </Link>
          
          <Link to="/analyses?mentioned_prefecture=true" className="action-card">
            <div className="action-icon">🏛️</div>
            <div className="action-content">
              <h4>Menções à Prefeitura</h4>
              <p>Análises que mencionam a prefeitura</p>
            </div>
          </Link>
        </div>
      </div>

      {/* Informações do sistema */}
      <div className="system-info">
        <h3>Informações do Sistema</h3>
        <div className="info-grid">
          <div className="info-item">
            <label>Última atualização:</label>
            <span>{formatDate(new Date().toISOString())}</span>
          </div>
          <div className="info-item">
            <label>Status:</label>
            <span className="status-online">🟢 Online</span>
          </div>
          <div className="info-item">
            <label>Versão:</label>
            <span>1.0.0</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard; 